import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Set default authorization header
const token = localStorage.getItem('token');
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Footer settings (public read, admin write)
export const getFooterSettings = () => axios.get(`${API}/footer/settings`);
export const updateFooterSettings = (payload) => axios.put(`${API}/admin/footer/settings`, payload);

// WebTrader Settings
export const getWebTraderSettings = () => axios.get(`${API}/webtrader/settings`);

// Market Data
export const getInstruments = (assetClass) => 
  axios.get(`${API}/market/instruments${assetClass ? `?asset_class=${assetClass}` : ''}`);

export const getCandles = (symbol, asset, interval, start, end) => 
  axios.get(`${API}/market/candles`, { params: { symbol, asset, interval, start, end } });

export const getQuote = (symbol, asset) => 
  axios.get(`${API}/market/quote/${symbol}`, { params: { asset } });

// Trading
export const createOrder = (orderData) => 
  axios.post(`${API}/trading/orders`, orderData);

export const getOrders = () => 
  axios.get(`${API}/trading/orders`);

export const getPositions = () => 
  axios.get(`${API}/trading/positions`);

export const closePosition = (positionId) => 
  axios.post(`${API}/trading/positions/${positionId}/close`);

export const getTradeHistory = (limit = 200) =>
  axios.get(`${API}/trading/history`, { params: { limit } });

// Accounts
export const getAccounts = () => 
  axios.get(`${API}/accounts`);

export const getAccount = (accountId) => 
  axios.get(`${API}/accounts/${accountId}`);

// Transactions (method-aware: CARD, WIRE, CRYPTO)
export const submitDeposit = ({ amount, method, currency = 'USD', metadata = {} }) =>
  axios.post(`${API}/transactions/deposit`, { amount, method, currency, metadata });

export const submitWithdrawal = ({ amount, method, currency = 'USD', metadata = {} }) =>
  axios.post(`${API}/transactions/withdraw`, { amount, method, currency, metadata });

// Backward compat
export const createDeposit = (amount, notes) =>
  axios.post(`${API}/transactions/deposit`, { amount, method: 'CARD', currency: 'USD', metadata: { notes } });

export const createWithdrawal = (amount, notes) =>
  axios.post(`${API}/transactions/withdraw`, { amount, method: 'WIRE', currency: 'USD', metadata: { notes } });

export const getTransactions = () =>
  axios.get(`${API}/transactions`);

// Watchlist
export const getWatchlist = () => axios.get(`${API}/client/watchlist`);
export const putWatchlist = (symbols) => axios.put(`${API}/client/watchlist`, { symbols });
export const addToWatchlist = (symbol) => axios.post(`${API}/client/watchlist/${symbol}`);
export const removeFromWatchlist = (symbol) => axios.delete(`${API}/client/watchlist/${symbol}`);
export const resetWatchlist = () => axios.post(`${API}/client/watchlist/reset`);

// KYC
export const uploadKYCDocument = (documentType, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post(`${API}/kyc/upload?document_type=${documentType}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const getKYCDocuments = () =>
  axios.get(`${API}/kyc/documents`);

export const downloadKYCDocument = (documentId) =>
  axios.get(`${API}/kyc/documents/${documentId}/download`, { responseType: 'blob' });

// Transaction proof-of-payment (wire transfer receipts)
export const uploadTransactionProof = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post(`${API}/transactions/upload-proof`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const downloadTransactionProof = (transactionId) =>
  axios.get(`${API}/transactions/${transactionId}/proof/download`, { responseType: 'blob' });

// Support
export const createTicket = (subject, message, priority) => 
  axios.post(`${API}/support/tickets`, { subject, message, priority });

export const getTickets = () => 
  axios.get(`${API}/support/tickets`);

export const getTicketMessages = (ticketId) => 
  axios.get(`${API}/support/tickets/${ticketId}/messages`);

export const addTicketMessage = (ticketId, message) => 
  axios.post(`${API}/support/tickets/${ticketId}/messages`, { message });

// Admin
export const getClients = () => 
  axios.get(`${API}/admin/clients`);

export const getClientDetails = (clientId) => 
  axios.get(`${API}/admin/clients/${clientId}`);

export const updateClientStatus = (clientId, isActive, reason) => 
  axios.put(`${API}/admin/clients/${clientId}/status`, { is_active: isActive, reason });

export const getAllTransactions = () => 
  axios.get(`${API}/admin/transactions`);

export const updateTransactionStatus = (transactionId, status, adminNotes) => 
  axios.put(`${API}/admin/transactions/${transactionId}`, { status, admin_notes: adminNotes });

export const getPendingKYC = () => 
  axios.get(`${API}/admin/kyc/pending`);

export const updateKYCStatus = (documentId, status, adminNotes) => 
  axios.put(`${API}/admin/kyc/${documentId}`, { status, admin_notes: adminNotes });

export const getAuditLogs = () => 
  axios.get(`${API}/admin/audit-logs`);

export const createInstrument = (instrumentData) => 
  axios.post(`${API}/admin/instruments`, instrumentData);

export default axios;